import { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Drawer,
  TextField,
  MenuItem,
  Button,
  Slider,
  FormControlLabel,
  Switch,
} from '@material-ui/core';
import {
  IconPackage,
  IconInfoCircle,
  IconShieldCheck,
  IconCheck,
  IconChevronDown,
  IconBulb,
  IconHomeCheck,
  IconUserCheck,
  IconPlus,
} from '@tabler/icons';

import { useAuth, useResponsive } from '#root/hooks';
import { userService, shippingService } from '#root/services';
import { USER_ADDRESS } from '#root/constants/navigation';
import ShippingDTO, { DocumentType, DocumentTypeValue } from '#root/models/Shipping';
import {
  AddressDTO,
  addressLocationLine,
  addressReceiverLine,
  hasDocument,
} from '#root/models/Address';
import {
  clampValorem,
  formatCurrency,
  insuranceCost,
  isValidDocNumber,
  valoremFromIndex,
  valoremSteps,
  valoremToIndex,
} from '#root/utils';
import { Button as DsButton } from '#root/components/ui/primitives';
import AddressDocumentFields from '../AddressDocumentFields';
import type { AddressDocumentValue } from '../AddressDocumentFields';
import DrawerAppbar from '../DrawerAppbar';
import ShipmentStatusBadge from '../../views/UserShipments/ShipmentStatusBadge';
import styles from './styles.module.scss';

interface ShippingAddressDrawerProps {
  open: boolean;
  onClose: () => void;
  shipping: ShippingDTO | null;
  onSuccess: () => void;
  createMessage: {
    success: (msg: string) => void;
    error: (msg: string) => void;
  };
}

const ShippingAddressDrawer = (props: ShippingAddressDrawerProps) => {
  const { open, onClose, shipping, onSuccess, createMessage } = props;
  const { isDesktop } = useResponsive();
  const { usuario } = useAuth();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addresses, setAddresses] = useState<AddressDTO[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | ''>('');
  const [docType, setDocType] = useState<DocumentTypeValue>(DocumentType.dni);
  const [docNumber, setDocNumber] = useState('');
  const [valorem, setValorem] = useState<number>(0);
  const [insured, setInsured] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  // Descarta la respuesta de direcciones de una apertura anterior (otro envío, otro hint)
  const addressesRequestRef = useRef(0);

  const hasAddresses = addresses.length > 0;
  const selectedAddress = addresses.find((item) => item.id === selectedAddressId);
  // Dirección sin documento de quien recibe: se completa aquí y viaja en la confirmación
  const needsDocument = !!selectedAddress && !hasDocument(selectedAddress);
  const isSubmitDisabled =
    !selectedAddressId || submitting || (needsDocument && !isValidDocNumber(docType, docNumber));
  // "Recibe: nombre · documento" (nombre guardado en la dirección; el de la cuenta si viene vacío)
  const accountName = usuario ? `${usuario.nom} ${usuario.ape}` : '';
  const receiverLine = addressReceiverLine(selectedAddress, accountName);
  // Valor elegido del selector: una línea (el selector la corta con elipsis antes de la flecha)
  const selectedAddressText = selectedAddress
    ? [selectedAddress.address_1?.trim(), addressLocationLine(selectedAddress)]
        .filter(Boolean)
        .join(', ')
    : '';
  // Precarga de la configuración del envío (caso mixto): la nota solo mientras siga elegida esa dirección
  const hintAddressId = shipping?.preconfirm_hint?.delivery_address_id;
  const isHintAddressSelected = !!hintAddressId && selectedAddressId === hintAddressId;

  const pickupOfficeName = shipping?.pickup_office?.name || 'OLVA';
  const pickupOfficeAddress = shipping?.pickup_office?.address;
  const trackingCode = shipping?.carrier_tracking_code || '---';

  // Valorem range logic
  const valoremRange = shipping?.valorem_range;
  const canShowValorem = valoremRange?.enabled === true;
  const valoremMin = valoremRange?.min ?? 0;
  const valoremMax = valoremRange?.max ?? 0;
  // Slider por índice (0..N) para llegar exacto al máximo; N = 0 (min = max) → monto fijo
  const sliderSteps = valoremSteps(valoremMin, valoremMax);
  // Seguro (D15): obligatorio si el envío supera S/ 1,000; si no, lo decide el interruptor "Asegurar"
  const isRequired = !!valoremRange?.required;
  const isInsured = canShowValorem && (isRequired || insured);
  const insuranceFee = isInsured
    ? insuranceCost(valorem, valoremRange.insurance_rate, valoremRange.insurance_igv)
    : 0;

  const handleClose = () => {
    onClose();
  };

  const handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedAddressId(Number(event.target.value));
    // El documento tecleado era para la dirección anterior
    setDocType(DocumentType.dni);
    setDocNumber('');
  };

  const handleDocumentChange = (value: AddressDocumentValue) => {
    setDocType(value.docType);
    setDocNumber(value.docNumber);
  };

  // El slider entrega un índice; el estado guarda el monto en soles
  const handleValoremChange = (_event: React.ChangeEvent<{}>, newValue: number | number[]) => {
    setValorem(valoremFromIndex(newValue as number, valoremMin, valoremMax));
  };

  const handleSubmit = async () => {
    if (!shipping || !selectedAddressId) return;

    setSubmitting(true);
    // Sin seguro (interruptor apagado o rango deshabilitado) no se manda valorem
    const valoremValue = isInsured && valorem > 0 ? valorem : undefined;
    // Documento de quien recibe: solo si la dirección elegida aún no lo tiene
    const receiverDocument = needsDocument
      ? { document_type: docType, document_number: docNumber }
      : undefined;

    const res = await shippingService.confirmAddress(
      shipping.id,
      selectedAddressId,
      valoremValue,
      receiverDocument
    );

    if (!res.error) {
      createMessage.success(res.message || 'Envío confirmado correctamente');
      onSuccess();
      handleClose();
    } else {
      createMessage.error(res.message || 'No se pudo confirmar el envío');
    }

    setSubmitting(false);
  };

  const fetchAddresses = async () => {
    const requestId = ++addressesRequestRef.current;
    setLoading(true);
    const res = await userService.address();
    if (requestId !== addressesRequestRef.current) return;
    if (!res.error) {
      const list: AddressDTO[] = res.data?.addresses_list || [];
      setAddresses(list);
      // Dirección de la configuración del envío, si sigue en la lista
      const hintId = shipping?.preconfirm_hint?.delivery_address_id;
      if (hintId && list.some((item) => item.id === hintId)) {
        setSelectedAddressId(hintId);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      fetchAddresses();
      setSelectedAddressId('');
      setDocType(DocumentType.dni);
      setDocNumber('');
      // Valorización inicial: la de la configuración del envío (acotada al rango) o el mínimo.
      // El costo del seguro se deriva en el render.
      const range = shipping?.valorem_range;
      const hint = shipping?.preconfirm_hint;
      if (range?.enabled) {
        setValorem(hint?.valorem ? clampValorem(hint.valorem, range.min, range.max) : range.min);
      } else {
        setValorem(0);
      }
      // "Asegurar" arranca apagado si el comprador configuró la dirección sin seguro
      setInsured(!(hint?.delivery_address_id && hint.valorem == null));
    }
  }, [open, shipping]);

  const content = (
    <div className={styles.main}>
      {/* Status Badge */}
      <div className={styles.statusHeader}>
        <ShipmentStatusBadge
          status={shipping?.current_status}
          paid_at={shipping?.paid_at}
          shipped_at={shipping?.shipped_at}
        />
      </div>

      {/* Pickup Office Info Box */}
      <div className={styles.infoBox}>
        <div className={styles.icon}>
          <IconPackage />
        </div>
        <div className={styles.content}>
          <p>Tu paquete está en <strong>{pickupOfficeName}</strong></p>
          {pickupOfficeAddress && <p className={styles.address}>{pickupOfficeAddress}</p>}
          {shipping?.carrier_tracking_code && (
            <p>Seguimiento #{shipping.carrier_tracking_code}</p>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className={styles.summary}>
        <h4>Resumen:</h4>
        <p className={styles.summaryDescription}>{shipping?.description || '---'}</p>
        {shipping?.valorem_range?.deals_total > 0 && (
          <p className={styles.summaryValue}>
            Valor aprox: <strong>{formatCurrency(shipping.valorem_range.deals_total, 0)}</strong>
          </p>
        )}
      </div>

      <hr />

      {/* Address Selection */}
      <div className={`${styles.section} ${styles.insuranceSection}`}>
        <h4 className={styles.sectionTitle}>
          <IconHomeCheck size={20} />
          Selecciona tu dirección
          </h4>

        {isHintAddressSelected && (
          <div className={styles.reassuranceBox}>
            <IconCheck size={16} />
            <span>Precargamos la configuración de envío que elegiste.</span>
          </div>
        )}

        {!selectedAddressId && hasAddresses && (
          <div className={styles.helperText}>
            <IconInfoCircle />
            <p>Selecciona tu dirección o crea una nueva.</p>
          </div>
        )}

        {!hasAddresses && !loading && (
          <div className={styles.helperText}>
            <IconInfoCircle />
            <p>Crea una dirección y luego selecciónala.</p>
          </div>
        )}

        {hasAddresses ? (
          <>
            <div className={styles.addressSelect}>
              <TextField
                select
                fullWidth
                size="medium"
                label="Elige la dirección"
                variant="outlined"
                value={selectedAddressId}
                onChange={handleAddressChange}
                disabled={loading}
                SelectProps={{
                  renderValue: () => selectedAddressText,
                  // En el menú, cada opción completa (con salto de línea) y del ancho del drawer
                  MenuProps: { PaperProps: { className: styles.addressMenu } },
                }}
              >
                {addresses.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <div className={styles.addressOption}>
                      <span>{item.address_1}</span>
                      <span style={{ fontSize: '12px', color: 'var(--color-text-alt)' }}>
                        {addressLocationLine(item)}
                      </span>
                    </div>
                  </MenuItem>
                ))}
              </TextField>
            </div>
            {selectedAddress && (
              <div className={styles.receiver}>
                <IconUserCheck size={16} />
                <span>Recibe: {receiverLine}</span>
              </div>
            )}
            {needsDocument && (
              <div className={styles.documentFields}>
                <AddressDocumentFields
                  docType={docType}
                  docNumber={docNumber}
                  onChange={handleDocumentChange}
                  disabled={submitting}
                />
              </div>
            )}
            <div className={styles.addAddress}>
              <DsButton variant="link" size="sm" icon={<IconPlus size={16} />} to={USER_ADDRESS}>
                Agregar otra dirección
              </DsButton>
            </div>
          </>
        ) : (
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            component={RouterLink}
            to={USER_ADDRESS}
          >
            Crear nueva dirección
          </Button>
        )}
      </div>

      {/* Valorem Section - Only show if enabled */}
      {canShowValorem && (
        <div className={`${styles.section} ${styles.insuranceSection}`}>
          {/* Título */}
          <h4 className={styles.sectionTitle}>
            <IconShieldCheck size={20} />
            Valorización del envío
          </h4>

          {/* Mensaje de tranquilidad */}
          <div className={styles.reassuranceBox}>
            <IconCheck size={16} />
            <span>Más del 99% de envíos llegan sin problemas</span>
          </div>

          {/* Obligatorio (D15) o interruptor "Asegurar" */}
          {isRequired ? (
            <div className={styles.helperText}>
              <IconInfoCircle />
              <p>Obligatorio para envíos de más de S/ 1,000.</p>
            </div>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={insured}
                  onChange={(event) => setInsured(event.target.checked)}
                  disabled={submitting}
                />
              }
              label="Asegurar"
            />
          )}

          {/* Slider con etiqueta (con min = max no hay slider: queda el monto fijo) */}
          {isInsured && (
            <div className={styles.sliderSection}>
              <span className={styles.sliderLabel}>Valor a asegurar</span>
              <div className={styles.valoremDisplay}>
                <span className={styles.valoremValue}>{formatCurrency(valorem, 0)}</span>
              </div>
              {sliderSteps > 0 && (
                <div className={styles.valoremSlider}>
                  {/* scale: aria-valuemin/max/now y getAriaValueText en soles, no en índices */}
                  <Slider
                    value={valoremToIndex(valorem, valoremMin, valoremMax)}
                    onChange={handleValoremChange}
                    min={0}
                    max={sliderSteps}
                    step={1}
                    scale={(index) => valoremFromIndex(index, valoremMin, valoremMax)}
                    getAriaValueText={(value) => formatCurrency(value, 0)}
                    aria-label="Valor a asegurar"
                    valueLabelDisplay="off"
                  />
                  <div className={styles.valoremRange}>
                    <span>{formatCurrency(valoremMin)}</span>
                    <span>{formatCurrency(valoremMax)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Costo del seguro */}
          {insuranceFee > 0 && (
            <div className={styles.insuranceCost}>
              <span className={styles.insuranceLabel}>Costo del seguro:</span>
              <span className={styles.insuranceValue}>{formatCurrency(insuranceFee)}</span>
              <span className={styles.insuranceNote}>(inc. IGV)</span>
            </div>
          )}

          {/* Expandible: ¿Cómo funciona? */}
          <button
            type="button"
            className={`${styles.detailsToggle} ${showDetails ? styles.expanded : ''}`}
            onClick={() => setShowDetails(!showDetails)}
          >
            <IconInfoCircle size={16} />
            <span>¿Cómo funciona?</span>
            <IconChevronDown size={16} className={showDetails ? styles.rotated : ''} />
          </button>

          {showDetails && (
            <div className={styles.detailsContent}>
              <ul className={styles.rulesList}>
                <li>
                  <strong>Obligatorio</strong> para envíos de más de S/ 1,000
                </li>
                <li>
                  <strong>Recomendado</strong> para pedidos mayores a S/ 500
                </li>
                <li>Puedes asegurar por un monto menor al total</li>
                <li>Cubre hasta el 95% del valor declarado</li>
              </ul>

              <div className={styles.ratesInfo}>
                <span className={styles.ratesTitle}>
                  Tarifas (fijadas por OLVA, según el valor total del envío):
                </span>
                <ul>
                  <li>S/ 100 – S/ 3,000 → 0.6 %</li>
                  <li>Más de S/ 3,000 → 2 %</li>
                </ul>
              </div>

              <div className={styles.costBreakdown}>
                <IconBulb size={16} />
                <span>El costo total de envío incluye: embalaje + flete + seguro</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className={styles.actions}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {submitting ? 'Confirmando...' : 'Confirmar Envío'}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {isDesktop ? (
        <Drawer
          anchor="right"
          open={open}
          onClose={handleClose}
          PaperProps={{
            elevation: 0,
            style: {
              background: 'var(--color-background-base)',
              width: 420,
              height: '100%',
            },
          }}
        >
          <DrawerAppbar title={`Envío #${shipping?.id || ''}`} onClose={handleClose} />
          {content}
        </Drawer>
      ) : (
        <Drawer
          anchor="right"
          open={open}
          onClose={handleClose}
          PaperProps={{
            style: {
              width: '100%',
              background: 'var(--color-background-base)',
            },
          }}
          style={{ zIndex: 1300 }}
        >
          <DrawerAppbar isMobile title={`Envío #${shipping?.id || ''}`} onClose={handleClose} />
          {content}
        </Drawer>
      )}
    </>
  );
};

export default ShippingAddressDrawer;
